import { action, computed, observable, toJS } from 'mobx';
import moment from 'moment';
import { groupBy as lodashGroupBy, isEqual } from 'lodash';
import Promise from 'bluebird';

import $auth from 'app/stores/$auth';
import $alerting from 'app/stores/alerting/$alerting';
import $mkp from 'app/stores/mkp/$mkp';
import groupByDay from 'core/util/groupByDay';
import { DEFAULT_DATETIME_FORMAT, timezone } from 'core/util/dateUtils';
import PagedCollection from 'core/model/PagedCollection';
import { isValidUUID } from 'app/views/alerting/util/uuid';
import {
  ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY,
  ALERT_STATE_API_TO_UI,
  ALERT_STATE_UI_TO_API,
  ALERTING_PER_PAGE_COUNT_DEFAULT,
  POLICY_APPLICATIONS,
  STANDARD_ALERT_SEVERITY_TO_ALERT_MANAGER_SEVERITY
} from '@kentik/ui-shared/alerting/constants';
import { checkApplication } from '@kentik/ui-shared/alerting/applicationUtils';

import AlertModel from './AlertModel';

const defaultServerFilter = {
  ackStates: [],
  ackedByUserIds: [],
  active: {
    start: moment.utc().subtract(24, 'hours').toISOString(),
    end: moment.utc().toISOString()
  },
  alarmIds: '',
  applications: [],
  includeSubpolicies: false,
  keyPartsSearch: '',
  lookback: 24 * 3600,
  pagination: {
    includeTotalCount: false,
    offset: 0
  },
  ruleIds: [],
  severities: [],
  sites: [],
  mitigationIds: [],
  states: ['ALARM_STATE_ACTIVE'],
  tenants: [],
  contextSearch: undefined
};

class AlertCollection extends PagedCollection {
  @observable.ref
  serverFilter = {
    ...defaultServerFilter
  };

  serverSortableFields = [
    'severity',
    'state', // ui field: stateLabel
    'applicationLabel',
    'policyName',
    'policyID',
    'primary_dimension', // ui field: primaryDimension
    'alarm_id', // ui field: id
    'start_time' // ui field: startTime
  ];

  uiSortLogicFields = ['policyName', 'policyID', 'applicationLabel'];

  @observable.ref
  policiesByRuleId = {};

  @observable.ref
  interfaceDescriptions = {};

  @observable.ref
  totals = {};

  @observable.ref
  totalCount = 0;

  constructor(data, options = { threeWaySort: true }) {
    super(data, options);
  }

  serverSortFn() {
    const sortBy = this.getServerSortBy();

    this.resetPageState();

    return Promise.all([
      this.fetch({
        data: {
          offset: 0,
          sortBy: sortBy || undefined
        },
        force: true
      }),
      $alerting.handleTotalCountLoad({ filters: this.getServerFilter() })
    ]);
  }

  get filterFieldWhitelist() {
    return new Set(['id', 'state', 'policyName', 'dimensionValues', 'tenant']);
  }

  get queuedFetchKey() {
    return this.id;
  }

  @action
  clearFilters() {
    this.serverFilter = {};
    super.clearFilters();

    this.queuedFetch();
  }

  get url() {
    return '/api/ui/alertingManager/alarms';
  }

  get fetchMethod() {
    return 'post';
  }

  get model() {
    return AlertModel;
  }

  @computed
  get countAckReq() {
    return this.unfiltered.filter((m) => m.isAckReq).length;
  }

  @computed
  get countAlarm() {
    return this.unfiltered.filter((m) => !m.isAckReq && !m.isCleared).length;
  }

  @computed
  get countCleared() {
    return this.unfiltered.filter((m) => m.isCleared).length;
  }

  @computed
  get attacksWithinLast24Hours() {
    return this.models.filter((model) => model.startTime >= moment.utc().subtract(24, 'hours').toISOString()).length;
  }

  @computed
  get activeAttacks() {
    return this.models.filter((model) => !model.isCleared).length;
  }

  getFetchOptions(options = {}) {
    const { data = {} } = options;
    const { pagination, sorting, includeSubpolicies, tenants = [], ...restServerFilter } = this.getServerFilter();
    const paginationOverride = {
      offset: data.offset ? data.offset : 0,
      limit: data.limit ?? (pagination?.limit || this.pageState.perPage || ALERTING_PER_PAGE_COUNT_DEFAULT),
      includeTotalCount: data.pagination?.includeTotalCount ?? pagination?.includeTotalCount ?? false
    };
    let { ruleIds = [], alarmIds = [] } = restServerFilter;

    if (alarmIds?.length > 0) {
      // Alert ID is highest specificity; ignore other filters
      const alarmIdsArray = Array.isArray(alarmIds) ? alarmIds : alarmIds.split(',');
      alarmIds = alarmIdsArray.map((v) => v.trim()).filter(isValidUUID);
      ruleIds = [];
    } else if (includeSubpolicies && ruleIds.length > 0) {
      // Always include parent rule IDs to prevent empty filter from being misinterpreted as "all tenant results".
      // Tenant filtering happens server-side based on tenant application scoping.
      ruleIds = ruleIds.concat($alerting.getChildrenRuleIds({ ruleIds, tenants }));
    }

    // restructure the payload by nesting the filters underneath "filter"
    return {
      ...options,
      data: {
        filter: {
          ...restServerFilter,
          tenants,
          alarmIds,
          ruleIds,
          includeSubpolicies,
          includeArchived: false
        },
        sorting,
        pagination: paginationOverride
      }
    };
  }

  getActiveAlertsChartData(groupBy = 'policyName') {
    if (this.size === 0) {
      return [];
    }

    // Group the models by Policy, because each series is a Policy
    // use `original` so the chart data never gets filtered
    const groupedByPolicy = lodashGroupBy(this.original, (m) => `${m[groupBy] || m.get(groupBy)}`);

    // For each policy, create a series object
    const series = Object.entries(groupedByPolicy).map(([identifier, policyModels]) => ({
      name: identifier,
      alertIds: policyModels.map((m) => m.id),
      y: policyModels.length
    }));

    return [
      {
        name: 'Alerts',
        data: series
      }
    ];
  }

  getAlertingOverviewData(groupBy = 'policyName') {
    if (this.size === 0) {
      return [];
    }

    // Group the models by Policy, because each series is a Policy
    // use `original` so the chart data never gets filtered
    const grouped = lodashGroupBy(this.original, (m) => `${m[groupBy] || m.get(groupBy)}`);

    // For each policy, create a series object
    return Object.entries(grouped).map(([identifier, policyModels]) => {
      // take the policyModels and group them into day buckets
      const groupedByDay = groupByDay(policyModels, 'start_time');

      // Convert the groupedByDay object into an array of { x: date, y: count, ...metadata } pairs
      const data = Object.entries(groupedByDay).map(([day, alertModelsForDay]) => {
        const utcDate = timezone.momentFn(day).valueOf();

        const count = alertModelsForDay.length;
        const alertIds = alertModelsForDay.map((m) => m.id);

        return { x: utcDate, y: count, alertIds, name: identifier };
      });

      return {
        name: identifier,
        data
      };
    });
  }

  async fetch(options = {}, method = 'fetch') {
    const promises = [];

    // we need to know about policies and tenants before we can set our fetch options
    if (!$alerting.policyCollection.hasFetched) {
      promises.push($alerting.policyCollection.fetch());
    }
    if (!$mkp.tenants.hasFetched) {
      promises.push($mkp.tenants.fetch());
    }

    // temporarily set to fetching so that the collection will look like it's loading
    this.requestStatus = 'fetching';

    return Promise.all(promises).then(() => {
      this.requestStatus = null; // reset back to null and let the collection set it as needed

      return super[method](this.getFetchOptions(options));
    });
  }

  async queuedFetch(options = {}) {
    const queuedFetchPromise = this.fetch(options, 'queuedFetch');
    this.lastFetched = Date.now();
    return queuedFetchPromise;
  }

  calculateServerFilter = (filter) => {
    const defaults = {
      ...defaultServerFilter,
      pagination: {
        ...defaultServerFilter.pagination,
        limit: filter.pagination?.limit || this.pageState.perPage || ALERTING_PER_PAGE_COUNT_DEFAULT
      }
    };
    const { startDate, endDate, ...restFilter } = filter;

    // adjust filter active date if this Fn was called with legacy startDate and endDate
    if (!filter.active && (startDate || endDate)) {
      restFilter.active = { start: startDate, end: endDate };
    }

    // apply some basic "false-y" defaults to both sides in order to have a more even comparison
    const existingValues = {
      ...defaults,
      ...toJS(this.serverFilter),
      lookback: filter?.looback || this.serverFilter.lookback
    };
    const newValues = { ...defaults, ...restFilter };

    // adjust values for state and severity
    if (Array.isArray(newValues.states)) {
      newValues.states = newValues.states
        .map((state) => ALERT_STATE_UI_TO_API[state] || state)
        .filter((state) => !!ALERT_STATE_API_TO_UI[state]);
    }
    if (Array.isArray(newValues.severities)) {
      newValues.severities = newValues.severities
        .map((severity) => STANDARD_ALERT_SEVERITY_TO_ALERT_MANAGER_SEVERITY[severity] || severity)
        .filter((severity) => !!ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[severity]);
    }

    // format start and end dates
    if (newValues.active?.start) {
      newValues.active.start = moment.utc(newValues.active.start).seconds(0).format();
    }
    if (newValues.active?.end) {
      newValues.active.end = moment.utc(newValues.active.end).seconds(0).format();
    }

    return { newValues, existingValues };
  };

  setServerFilter(filter, options = {}, fetchOptions = {}) {
    const { fetch = true, force } = options;
    const { newValues, existingValues } = this.calculateServerFilter(filter);

    // avoid unnecessary loads
    if (!force && isEqual(newValues, existingValues)) {
      return Promise.resolve();
    }

    /** last checks to optimize
     - if start or end times are within 2 minutes, consider them equal
     - this handles the timing discrepancies between store instantiations and filter sidebar / admin table loading
     - we do 2 minutes instead of 1, to also cover the corner case of rounding up vs down on the minute mark */
    if (!force && newValues.active?.start && existingValues.active?.start) {
      const newStart = moment.utc(newValues.active.start).seconds(0).milliseconds(0);
      const existingStart = moment.utc(existingValues.active.start).seconds(0).milliseconds(0);

      newValues.active.start = newStart.format(DEFAULT_DATETIME_FORMAT);
      existingValues.active.start = existingStart.format(DEFAULT_DATETIME_FORMAT);

      if (Math.abs(newStart.diff(existingStart, 'minutes')) <= 2) {
        newValues.active.start = existingValues.active.start;

        if (isEqual(newValues, existingValues)) {
          return Promise.resolve();
        }
      }
    }

    // same as above, but for end time
    if (!force && newValues.active?.end && existingValues.active?.end) {
      const newEnd = moment.utc(newValues.active.end).seconds(0).milliseconds(0);
      const existingEnd = moment.utc(existingValues.active.end).seconds(0).milliseconds(0);

      newValues.active.end = newEnd.format(DEFAULT_DATETIME_FORMAT);
      existingValues.active.end = existingEnd.format(DEFAULT_DATETIME_FORMAT);

      if (Math.abs(newEnd.diff(existingEnd, 'minutes')) <= 2) {
        newValues.active.end = existingValues.active.end;

        if (isEqual(newValues, existingValues)) {
          return Promise.resolve();
        }
      }
    }

    this.serverFilter = { ...newValues };

    const alarmIdsConditions =
      !newValues.alarmIds ||
      isValidUUID(newValues.alarmIds) ||
      newValues.alarmIds?.split(',').every((v) => isValidUUID(v.trim()));

    if (fetch && alarmIdsConditions) {
      return this.pagedFetch({ force, ...fetchOptions });
    }

    return Promise.resolve();
  }

  getGroupedKeys = (items) => {
    const groups = {};

    items.forEach((model) => {
      const group = model[this.groupBy] ?? model.get?.(this.groupBy);

      if (Array.isArray(group)) {
        if (group.length > 0) {
          group.forEach((g) => {
            groups[g] ??= [];
            groups[g].push(model);
          });
        } else {
          groups.None ??= [];
          groups.None.push(model);
        }
      } else {
        groups[group] ??= [];
        groups[group].push(model);
      }
    });

    return Object.keys(groups);
  };

  createAlertModelFromAlert = (alert, initial_context, policiesByRuleId) => {
    const policy = policiesByRuleId[alert.rule_id];

    policy.application = policy.application === '' ? POLICY_APPLICATIONS.CORE : policy.application;

    const model = new AlertModel();
    model.collection = this;
    model.set(model.deserialize({ alarm: alert, activation_context: initial_context, policy }));

    return model;
  };

  getGroupedDataFracturedKey = (currentPageData, nextPageData) => {
    if (!this.groupBy) {
      return undefined;
    }

    const currentPageGroupKeys = this.getGroupedKeys(
      currentPageData?.alarms?.map((a) =>
        this.createAlertModelFromAlert(a.alarm, a.initial_context, currentPageData.policiesByRuleId)
      ) || []
    );

    const nextPageGroupKeys = this.getGroupedKeys(
      nextPageData?.alarms?.map((a) =>
        this.createAlertModelFromAlert(a.alarm, a.initial_context, nextPageData.policiesByRuleId)
      ) || []
    );

    // if any of the group keys on the current page are also on the next page, then the data is fractured
    const fracturedKey = currentPageGroupKeys.filter((key) => nextPageGroupKeys.includes(key));

    if (fracturedKey.length > 0) {
      return fracturedKey[0];
    }

    return undefined;
  };

  @action
  selectAll() {
    this.selected = this.isAllSelected ? null : this.models.filter((model) => model.isSelectable);
  }

  @action
  group(value) {
    this.groupBy = value;

    const calculatedSortBy = this.getServerSortBy();

    return Promise.all([
      this.fetch({
        data: {
          offset: 0,
          limit: this.pageState.perPage || ALERTING_PER_PAGE_COUNT_DEFAULT,
          sortBy: calculatedSortBy || undefined
        },
        force: true
      }),
      $alerting.handleTotalCountLoad({ filters: this.getServerFilter() })
    ]).then(([results, totalsResults]) => {
      this.setPageState({
        groupedDataFracturedKey: this.getGroupedDataFracturedKey(results, totalsResults)
      });

      return [results, totalsResults];
    });
  }

  getServerFilter() {
    const {
      active,
      applications = [],
      ackStates = [],
      alarm,
      keyPartsSearch,
      ackedByUserIds = [],
      ...filter
    } = toJS(this.serverFilter);

    const { pageState } = this;

    filter.ackStates = [];
    filter.active = { start: null, end: moment.utc().toISOString() };
    filter.ackedByUserIds = [];
    filter.applications = applications || [];
    filter.keyPartsSearch = undefined;

    if (!filter.states) {
      filter.states = [];
    }

    // If the user is filtering by metric application, we need to include NMS as well
    if (
      filter.applications.some(checkApplication.isMetric) &&
      !filter.applications.some(checkApplication.isNms) &&
      $auth.hasPermission('alerts.nmsNative.enabled', { overrideForSudo: false })
    ) {
      filter.applications = [...filter.applications, POLICY_APPLICATIONS.NMS];
    }

    // If the user is filtering by metric application, we need to include EVENT as well
    if (
      filter.applications.some(checkApplication.isMetric) &&
      !filter.applications.some(checkApplication.isEvent) &&
      $auth.hasPermission('alerts.eventAlerting.enabled', { overrideForSudo: false })
    ) {
      filter.applications = [...filter.applications, POLICY_APPLICATIONS.KEVENT];
    }

    Object.entries(filter).forEach(([field, value]) => {
      // Permit empty arrays; api treats them as unspecified.
      if (!value) {
        delete filter[field];
      }
    });

    if (Number.isInteger(filter.lookback) && filter.lookback > 0) {
      filter.active.start = moment.utc().subtract(filter.lookback, 'seconds').toISOString();
    } else if (active?.start || active?.end) {
      filter.active.start = moment(active?.start).isValid() ? moment.utc(active.start).format() : null;
      filter.active.end = moment(active?.end).isValid() ? moment.utc(active.end).format() : null;
    }

    if (Array.isArray(ackedByUserIds) && ackedByUserIds.length > 0) {
      filter.ackedByUserIds = ackedByUserIds;
    }

    // These use API values, so we can apply them directly
    if (Array.isArray(ackStates)) {
      const validAckStates = [];
      ackStates.forEach((ackState) => {
        if (ackState === 'ACKED_BY_ME') {
          filter.ackedByUserIds = [$auth.activeUser.id];
        } else {
          validAckStates.push(ackState);
        }
      });

      filter.ackStates = validAckStates;
    }

    if (this.groupBy || (this.sortState.field && this.sortFieldIsServerSortable)) {
      filter.sorting = {
        fields: []
      };

      if (this.groupBy) {
        if (this.groupByFieldIsUISortLogicDriven) {
          filter.sorting.fields.push({
            name: 'rule_id',
            values: $alerting.getSortedRuleIds({ field: this.groupBy, direction: 'asc' })
          });
        } else {
          filter.sorting.fields.push({
            name: this.groupBy,
            order: 'SORT_ORDER_ASCENDING'
          });
        }
      }

      if (this.sortState.field && this.sortFieldIsServerSortable && this.sortState.field !== this.groupBy) {
        let sortDirection = 'SORT_ORDER_UNSPECIFIED';

        if (this.sortState.direction === 'asc') {
          sortDirection = 'SORT_ORDER_ASCENDING';
        } else if (this.sortState.direction === 'desc') {
          sortDirection = 'SORT_ORDER_DESCENDING';
        }

        if (this.sortFieldIsUISortLogicDriven) {
          filter.sorting.fields.push({
            name: 'rule_id',
            values: $alerting.getSortedRuleIds({
              field: this.sortState.field,
              direction: this.sortState.direction
            })
          });
        } else {
          filter.sorting.fields.push({
            name: this.sortState.field,
            order: sortDirection
          });
        }
      }
    }

    if (keyPartsSearch) {
      if (Array.isArray(keyPartsSearch)) {
        filter.keys = {
          filters: [
            {
              value: {
                in: { values: keyPartsSearch }
              }
            }
          ]
        };
      } else {
        filter.keys = {
          filters: [
            {
              key: { any: true },
              value: { contains: keyPartsSearch }
            }
          ]
        };
      }
    }

    filter.pagination = {
      offset: (pageState.currentPage - 1) * pageState.perPage,
      limit: pageState.perPage
    };

    return { ...filter };
  }

  deserialize(data = {}) {
    const { alarms = [], pagination = {}, policiesByRuleId = {}, interfaceDescriptions } = data;

    this.totalCount = Number(pagination?.total_count || alarms.length);
    this.policiesByRuleId = Object.assign(this.policiesByRuleId, policiesByRuleId);
    this.interfaceDescriptions = Object.assign(this.interfaceDescriptions, interfaceDescriptions);

    const uniqueAlarms = alarms
      .map(({ alarm, initial_context = {} }) => {
        const policy = this.policiesByRuleId[alarm.rule_id];

        if (!policy) {
          console.warn(
            'No matching policy found for alert id ',
            alarm.id,
            'with rule id ',
            alarm.rule_id,
            ' and application ',
            alarm.application
          );
          return null;
        }

        policy.application = policy.application === '' ? POLICY_APPLICATIONS.CORE : policy.application;

        return { alarm, activation_context: initial_context, policy };
      })
      .filter(Boolean); // filter out duds that don't have policy, can be because of corrupted data such as https://github.com/kentik/ui-app/issues/22927

    // Add policy to each alarm
    return super.deserialize(uniqueAlarms);
  }
}

export default AlertCollection;
